import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import {
  Building2,
  Users,
  Calculator,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  ArrowRight,
  Star,
  Clock,
  CheckCircle2,
} from 'lucide-react';

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

const statusColor = {
  disponivel: 'badge-disponivel',
  em_negociacao: 'badge-negociacao',
  vendido: 'badge-vendido',
  alugado: 'badge-alugado',
};
const statusLabel = {
  disponivel: 'Disponível',
  em_negociacao: 'Em negociação',
  vendido: 'Vendido',
  alugado: 'Alugado',
};
const leadStatusColor = {
  novo: 'badge-novo',
  contato: 'badge-contato',
  visita: 'badge-visita',
  proposta: 'badge-proposta',
  fechado: 'badge-fechado',
  perdido: 'badge-perdido',
};


function IndicesMercado() {
  const [indices, setIndices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [atualizadoEm, setAtualizadoEm] = React.useState(null);

  const carregar = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/indices/dashboard');
      setIndices(res.data.indices || []);
      setAtualizadoEm(new Date());
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { carregar(); }, [carregar]);

  const grupos = {
    renda_fixa: { label: 'Renda Fixa',      cor: 'text-blue-700',    bg: 'bg-blue-50'    },
    macro:      { label: 'Inflação',         cor: 'text-amber-700',   bg: 'bg-amber-50'   },
    construcao: { label: 'Construção Civil', cor: 'text-emerald-700', bg: 'bg-emerald-50' },
    agro:       { label: 'Agro (CEPEA)',     cor: 'text-lime-700',    bg: 'bg-lime-50'    },
  };

  const fmtVal = (v, u) => {
    if (v == null) return '—';
    if (u === 'R$/m²') return `R$ ${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
    if (u?.startsWith('R$')) return `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return `${Number(v).toFixed(2)}%`;
  };

  const gruposOrdenados = ['renda_fixa', 'macro', 'construcao', 'agro'];
  const porGrupo = gruposOrdenados
    .map(tipo => ({ tipo, meta: grupos[tipo], itens: indices.filter(i => i.tipo === tipo) }))
    .filter(g => g.itens.length > 0);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={15} className="text-[#064E3B]" />
          <h2 className="text-sm font-semibold text-stone-700">Índices do Mercado</h2>
          {atualizadoEm && (
            <span className="text-xs text-stone-400">
              · {atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <button
          onClick={carregar}
          disabled={loading}
          className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1 transition-colors"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-stone-200 rounded-lg p-4 text-center text-stone-400 text-sm">
          Carregando índices...
        </div>
      ) : (
        <div className="space-y-3">
          {porGrupo.map(({ tipo, meta, itens }) => (
            <div key={tipo} className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 border-b border-stone-100">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.cor}`}>
                  {meta.label}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 divide-x divide-y divide-stone-100">
                {itens.map((idx) => {
                  const varDia = idx.variacao_dia;
                  return (
                    <div key={idx.label} className="px-4 py-3">
                      <p className="text-[10px] text-stone-400 font-medium mb-0.5">{idx.label}</p>
                      <p className={`text-base font-bold ${idx.valor != null ? 'text-stone-900' : 'text-stone-300'}`}>
                        {fmtVal(idx.valor, idx.unidade)}
                      </p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <p className="text-[10px] text-stone-400">{idx.unidade}</p>
                        {varDia != null && (
                          <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${
                            varDia > 0 ? 'text-emerald-600' : varDia < 0 ? 'text-red-500' : 'text-stone-400'
                          }`}>
                            {varDia > 0 ? <TrendingUp size={9} /> : varDia < 0 ? <TrendingDown size={9} /> : <Minus size={9} />}
                            {varDia > 0 ? '+' : ''}{Number(varDia).toFixed(2)}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ properties: null, leads: null });
  const [recentProps, setRecentProps] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pStats, lStats, props, leads] = await Promise.all([
          api.get('/properties/stats'),
          api.get('/leads/stats'),
          api.get('/properties'),
          api.get('/leads'),
        ]);
        setStats({ properties: pStats.data, leads: lStats.data });
        setRecentProps((props.data || []).slice(0, 4));
        setRecentLeads((leads.data || []).slice(0, 5));
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const kpis = [
    {
      label: 'Imóveis Disponíveis',
      value: stats.properties?.disponivel ?? '—',
      sub: `${stats.properties?.total ?? 0} no total`,
      icon: Building2,
      color: 'text-[#064E3B]',
      bg: 'bg-[#064E3B]/10',
      link: '/imoveis',
    },
    {
      label: 'Leads Ativos',
      value: (stats.leads?.novo ?? 0) + (stats.leads?.contato ?? 0) + (stats.leads?.visita ?? 0),
      sub: `${stats.leads?.fechado ?? 0} fechados`,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      link: '/leads',
    },
    {
      label: 'Simulações do Mês',
      value: user?.simulacoes_mes ?? 0,
      sub: user?.limite_simulacoes_mes === -1 ? 'Ilimitadas' : `de ${user?.limite_simulacoes_mes ?? 10}`,
      icon: Calculator,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      link: '/calculadora',
    },
    {
      label: 'Em Destaque',
      value: stats.properties?.destaque ?? '—',
      sub: 'imóveis em destaque',
      icon: Star,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      link: '/imoveis',
    },
  ];

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content p-6 lg:p-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="text-3xl font-bold text-stone-900 mb-1"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="dashboard-title"
          >
            Olá, {user?.name?.split(' ')[0] || 'Corretor'}!
          </h1>
          <p className="text-stone-500 text-sm">
            {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((k) => (
            <Link
              key={k.label}
              to={k.link}
              className="bg-white border border-stone-200 rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-md transition-all group"
              data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, '-')}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg ${k.bg} flex items-center justify-center`}>
                  <k.icon size={18} className={k.color} />
                </div>
                <ArrowRight size={14} className="text-stone-300 group-hover:text-stone-500 transition-colors mt-1" />
              </div>
              <p className="text-2xl font-bold text-stone-900">{loading ? '—' : k.value}</p>
              <p className="text-xs font-semibold text-stone-500 mt-0.5">{k.label}</p>
              <p className="text-xs text-stone-400 mt-0.5">{k.sub}</p>
            </Link>
          ))}
        </div>

        <IndicesMercado />

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Properties */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <h2 className="font-semibold text-stone-800 text-sm">Imóveis Recentes</h2>
              <Link to="/imoveis" className="text-xs text-[#064E3B] font-medium hover:underline flex items-center gap-1">
                Ver todos <ArrowRight size={12} />
              </Link>
            </div>
            {loading ? (
              <div className="p-8 text-center text-stone-400 text-sm">Carregando...</div>
            ) : recentProps.length === 0 ? (
              <div className="p-8 text-center text-stone-400 text-sm">Nenhum imóvel cadastrado</div>
            ) : (
              <ul className="divide-y divide-stone-50">
                {recentProps.map((p) => (
                  <li key={p.id} className="px-5 py-3.5 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate max-w-[200px]">{p.titulo}</p>
                      <p className="text-xs text-stone-400 mt-0.5">{p.bairro}, {p.cidade}</p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-sm font-semibold text-stone-900">{formatCurrency(p.valor)}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${statusColor[p.status] || ''}`}>
                        {statusLabel[p.status] || p.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Recent Leads */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <h2 className="font-semibold text-stone-800 text-sm">Leads Recentes</h2>
              <Link to="/leads" className="text-xs text-[#064E3B] font-medium hover:underline flex items-center gap-1">
                Ver todos <ArrowRight size={12} />
              </Link>
            </div>
            {loading ? (
              <div className="p-8 text-center text-stone-400 text-sm">Carregando...</div>
            ) : recentLeads.length === 0 ? (
              <div className="p-8 text-center text-stone-400 text-sm">Nenhum lead cadastrado</div>
            ) : (
              <ul className="divide-y divide-stone-50">
                {recentLeads.map((l) => (
                  <li key={l.id} className="px-5 py-3.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-stone-500">
                          {(l.nome || l.name || 'L').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-800">{l.nome || l.name}</p>
                        <p className="text-xs text-stone-400">{l.email || l.telefone || '—'}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${leadStatusColor[l.status] || 'badge-novo'}`}>
                      {l.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { to: '/calculadora', label: 'Nova Simulação', icon: Calculator, color: 'bg-[#064E3B]' },
            { to: '/imoveis', label: 'Cadastrar Imóvel', icon: Building2, color: 'bg-stone-800' },
            { to: '/leads', label: 'Novo Lead', icon: Users, color: 'bg-blue-700' },
          ].map((a) => (
            <Link
              key={a.to}
              to={a.to}
              className={`${a.color} text-white rounded-lg px-4 py-3 flex items-center gap-3 hover:opacity-90 transition-opacity`}
            >
              <a.icon size={18} />
              <span className="text-sm font-semibold">{a.label}</span>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
